import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { FiPlay, FiX } from "react-icons/fi";
import { useLanguage } from "../Language";
import { exampleGroups } from "./ExamplesData";
import "./Examples.css";

const clips = exampleGroups.flatMap((group) =>
  group.videos.map((clip) => ({ ...clip, group })),
);

function ExamplePreview({
  clip,
  active,
  selected,
  allowAutoplay,
  playRequested,
  onWatch,
}) {
  const { t } = useLanguage();
  const frame = useRef(null);
  const media = useRef(null);
  const [visible, setVisible] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [manualPlayback, setManualPlayback] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [failed, setFailed] = useState(false);
  const [aspectRatio, setAspectRatio] = useState(9 / 16);

  const requestPlay = () => {
    const bounds = frame.current.getBoundingClientRect();
    const visibleHeight =
      Math.min(window.innerHeight, bounds.bottom) - Math.max(0, bounds.top);
    if (visibleHeight < bounds.height * 0.55)
      frame.current.scrollIntoView({ block: "center", behavior: "auto" });
    setVisible(true);
    setLoaded(true);
    setManualPlayback(true);
  };

  useEffect(() => {
    if (active && playRequested) {
      setLoaded(true);
      setManualPlayback(true);
    }
  }, [active, playRequested]);

  useEffect(() => {
    if (!window.IntersectionObserver) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        const inView = entry.isIntersecting && entry.intersectionRatio >= 0.55;
        setVisible(inView);
        if (inView && allowAutoplay && active) setLoaded(true);
      },
      { threshold: [0, 0.55] },
    );
    observer.observe(frame.current);
    return () => observer.disconnect();
  }, [allowAutoplay, active]);

  useEffect(() => {
    const video = media.current;
    if (!video || !loaded || failed) return;
    let cancelled = false;
    const shouldPlay = active && visible && (manualPlayback || allowAutoplay);
    if (shouldPlay) {
      video.play().catch((error) => {
        // Autoplay can be blocked by browser or power-saving settings; keep the play control available.
        if (!cancelled) {
          setPlaying(false);
          if (error.name !== "NotAllowedError" && error.name !== "AbortError")
            setFailed(true);
        }
      });
    } else {
      video.pause();
    }
    return () => {
      cancelled = true;
      video.pause();
    };
  }, [active, visible, loaded, manualPlayback, allowAutoplay, failed]);

  return (
    <figure className="showcase-preview" aria-hidden={!selected}>
      <div
        ref={frame}
        className={`example-video${failed ? " has-error" : ""}`}
        style={{ "--video-aspect-ratio": aspectRatio }}
        role="group"
        aria-label={clip.title}
      >
        {failed ? (
          <div className="example-video-error">
            <p role="alert">
              {t(
                "Video konnte nicht geladen werden. Bitte versucht es später erneut.",
              )}
            </p>
            <button
              className="action action-primary"
              onClick={() => {
                setFailed(false);
                requestPlay();
              }}
            >
              {t("Erneut versuchen")}
            </button>
          </div>
        ) : (
          <>
            <video
              ref={media}
              src={loaded ? clip.preview : undefined}
              poster={clip.poster}
              muted
              loop
              playsInline
              preload="none"
              aria-label={clip.title}
              onPlay={() => setPlaying(true)}
              onPause={() => setPlaying(false)}
              onError={() => {
                setPlaying(false);
                setFailed(true);
              }}
              onLoadedMetadata={(event) => {
                const video = event.currentTarget;
                if (video.videoWidth > 0 && video.videoHeight > 0)
                  setAspectRatio(video.videoWidth / video.videoHeight);
              }}
            />
            {selected && (
              <button
                className="preview-expand"
                aria-label={t("Ganzes Video ansehen")}
                aria-haspopup="dialog"
                aria-controls="showcase-viewer"
                onClick={(event) => onWatch(clip, event.currentTarget)}
              />
            )}
            {!playing && (
              <button
                className="preview-control"
                aria-label={t("Vorschau abspielen")}
                onClick={requestPlay}
              >
                <FiPlay aria-hidden="true" />
              </button>
            )}
          </>
        )}
      </div>
      <figcaption>
        <span>{clip.title}</span>
      </figcaption>
    </figure>
  );
}

const stageMedia =
  "(min-width: 761px) and (min-height: 800px) and (prefers-reduced-motion: no-preference)";

function Examples({ navigationKey }) {
  const { t } = useLanguage();
  const [allowAutoplay, setAllowAutoplay] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const section = useRef(null);
  const categoryCopy = useRef(null);
  const categoryVideos = useRef(null);
  const previousGroup = useRef(clips[0].group);
  const previousVideo = useRef(0);
  const videoCards = useRef([]);
  const stagePosition = useRef(null);
  const pendingStage = useRef(null);
  const [scrollStages, setScrollStages] = useState(
    () => window.matchMedia?.(stageMedia).matches ?? false,
  );
  const [userSelected, setUserSelected] = useState(null);
  const [pageVisible, setPageVisible] = useState(!document.hidden);
  const [viewer, setViewer] = useState(null);
  const [fullFailed, setFullFailed] = useState(false);
  const [fullAttempt, setFullAttempt] = useState(0);
  const [fullAspectRatio, setFullAspectRatio] = useState(9 / 16);
  const dialog = useRef(null);
  const viewerOpener = useRef(null);
  const fullVideo = useRef(null);

  useEffect(() => {
    if (!window.matchMedia) return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)");
    const connection = navigator.connection;
    const update = () => {
      setAllowAutoplay(!reduced.matches && !connection?.saveData);
    };
    update();
    reduced.addEventListener("change", update);
    connection?.addEventListener?.("change", update);
    return () => {
      reduced.removeEventListener("change", update);
      connection?.removeEventListener?.("change", update);
    };
  }, []);

  useEffect(() => {
    const update = () => {
      setPageVisible(!document.hidden);
      if (document.hidden) fullVideo.current?.pause();
    };
    document.addEventListener("visibilitychange", update);
    return () => document.removeEventListener("visibilitychange", update);
  }, []);

  const goTo = useCallback(
    (index, alignStage = true) => {
      const next = Math.max(0, Math.min(clips.length - 1, index));
      setActiveIndex(next);
      setUserSelected(null);
      if (alignStage && scrollStages) {
        const bounds = section.current.getBoundingClientRect();
        const nav =
          parseFloat(getComputedStyle(section.current).scrollMarginTop) || 96;
        const height = window.innerHeight - nav;
        if (
          bounds.top <= nav + 12 &&
          bounds.bottom >= window.innerHeight - 12
        ) {
          const category = exampleGroups.indexOf(clips[next].group);
          pendingStage.current = category;
          window.scrollTo({
            top: window.scrollY + bounds.top - nav + category * height,
            behavior: "smooth",
          });
        }
      }
    },
    [scrollStages],
  );

  useEffect(() => {
    if (!window.matchMedia) return;
    const preference = window.matchMedia(stageMedia);
    const update = () => setScrollStages(preference.matches);
    preference.addEventListener("change", update);
    return () => preference.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    if (!scrollStages || viewer) return;
    let frame;
    let timer;
    let origin;
    let lastY = window.scrollY;
    let touching = false;
    const geometry = () => {
      const nav =
        parseFloat(getComputedStyle(section.current).scrollMarginTop) || 96;
      return {
        start:
          window.scrollY + section.current.getBoundingClientRect().top - nav,
        height: window.innerHeight - nav,
      };
    };
    const cancel = () => {
      clearTimeout(timer);
      origin = undefined;
      touching = false;
      pendingStage.current = null;
    };
    const update = () => {
      frame = undefined;
      const { start, height } = geometry();
      const progress = (window.scrollY - start) / height;
      if (progress < -0.05 || progress > 2.05) return;
      const stage = Math.max(0, Math.min(2, Math.round(progress)));
      if (pendingStage.current !== null) {
        if (Math.abs(progress - pendingStage.current) > 0.02) return;
        pendingStage.current = null;
      }
      if (stagePosition.current !== stage) {
        stagePosition.current = stage;
        goTo(
          clips.findIndex((clip) => clip.group === exampleGroups[stage]),
          false,
        );
      }
    };
    const settle = () => {
      const from = origin;
      origin = undefined;
      if (from === undefined || Math.abs(window.scrollY - from) < 8) return;
      const { start, height } = geometry();
      const progress = (window.scrollY - start) / height;
      // Leave both ends freely; never pull visitors back from later sections.
      if (progress < -0.05 || progress > 2.05) return;
      let stage = Math.max(0, Math.min(2, Math.round(progress)));
      const initial = (from - start) / height;
      if (
        Math.abs(initial - Math.round(initial)) < 0.02 &&
        Math.abs(window.scrollY - from) >= 40
      )
        stage = Math.max(
          0,
          Math.min(2, Math.round(initial) + Math.sign(window.scrollY - from)),
        );
      window.scrollTo({ top: start + stage * height, behavior: "smooth" });
    };
    const scroll = () => {
      lastY = window.scrollY;
      if (origin !== undefined && !touching) {
        clearTimeout(timer);
        timer = setTimeout(settle, 140);
      }
      if (frame === undefined) frame = requestAnimationFrame(update);
    };
    const wheel = (event) => {
      if (
        event.ctrlKey ||
        event.defaultPrevented ||
        Math.abs(event.deltaX) >= Math.abs(event.deltaY)
      )
        return;
      pendingStage.current = null;
      clearTimeout(timer);
      if (origin === undefined) origin = lastY;
    };
    const touchStart = () => {
      touching = true;
      pendingStage.current = null;
      clearTimeout(timer);
      if (origin === undefined) origin = lastY;
    };
    const touchEnd = () => {
      touching = false;
      if (origin !== undefined) timer = setTimeout(settle, 140);
    };
    const key = (event) => {
      if (
        event.defaultPrevented ||
        event.ctrlKey ||
        event.metaKey ||
        event.altKey ||
        event.target.closest?.(
          "button, a, input, textarea, select, [contenteditable]",
        )
      )
        return;
      if (
        ["ArrowDown", "ArrowUp", "PageDown", "PageUp", " "].includes(event.key)
      ) {
        pendingStage.current = null;
        clearTimeout(timer);
        if (origin === undefined) origin = lastY;
      } else cancel();
    };
    window.addEventListener("scroll", scroll, { passive: true });
    window.addEventListener("wheel", wheel, { passive: true });
    window.addEventListener("touchstart", touchStart, { passive: true });
    window.addEventListener("touchend", touchEnd, { passive: true });
    window.addEventListener("touchcancel", cancel, { passive: true });
    document.addEventListener("keydown", key);
    document.addEventListener("click", cancel, true);
    window.addEventListener("resize", cancel);
    return () => {
      cancel();
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", scroll);
      window.removeEventListener("wheel", wheel);
      window.removeEventListener("touchstart", touchStart);
      window.removeEventListener("touchend", touchEnd);
      window.removeEventListener("touchcancel", cancel);
      document.removeEventListener("keydown", key);
      document.removeEventListener("click", cancel, true);
      window.removeEventListener("resize", cancel);
    };
  }, [scrollStages, navigationKey, viewer, goTo]);

  const watch = (clip, opener) => {
    viewerOpener.current = opener;
    setViewer(clip);
    setFullFailed(false);
    setFullAspectRatio(9 / 16);
    dialog.current.showModal();
  };
  const close = () => {
    fullVideo.current?.pause();
    dialog.current.close();
    setViewer(null);
    viewerOpener.current?.focus({ preventScroll: true });
  };
  const currentGroup = clips[activeIndex].group;

  useLayoutEffect(() => {
    const previous = previousGroup.current;
    previousGroup.current = currentGroup;
    if (
      previous === currentGroup ||
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ||
      !categoryVideos.current.animate
    )
      return;
    const direction = Math.sign(
      exampleGroups.indexOf(currentGroup) - exampleGroups.indexOf(previous),
    );
    const easing = "cubic-bezier(0.22, 1, 0.36, 1)";
    const copy = categoryCopy.current.animate(
      [
        { opacity: 0, transform: `translateY(${direction * 10}px)` },
        { opacity: 1, transform: "translateY(0)" },
      ],
      { duration: 420, easing },
    );
    const videos = categoryVideos.current.animate(
      [
        { opacity: 0, transform: `translateY(${direction * 18}px)` },
        { opacity: 1, transform: "translateY(0)" },
      ],
      { duration: 500, easing },
    );
    return () => {
      copy.cancel();
      videos.cancel();
    };
  }, [currentGroup]);

  useLayoutEffect(() => {
    const previous = previousVideo.current;
    previousVideo.current = activeIndex;
    if (
      previous === activeIndex ||
      clips[previous].group !== clips[activeIndex].group ||
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ||
      !videoCards.current[activeIndex].animate
    )
      return;
    const selected = videoCards.current[activeIndex];
    const outgoing = videoCards.current[previous];
    const distance = outgoing.offsetLeft - selected.offsetLeft;
    const timing = { duration: 560, easing: "cubic-bezier(0.22, 1, 0.36, 1)" };
    const front = selected.animate(
      [
        { transform: `translateX(${distance}px) scale(0.94)`, opacity: 0.55 },
        { transform: "translateX(0) scale(1)", opacity: 1 },
      ],
      timing,
    );
    const back = outgoing.animate(
      [
        { transform: `translateX(${-distance}px) scale(1)`, opacity: 1 },
        {
          transform: `translateX(${-distance * 0.45}px) scale(0.9)`,
          opacity: 0.45,
          offset: 0.5,
        },
        { transform: "translateX(0) scale(0.94)", opacity: 0.55 },
      ],
      timing,
    );
    return () => {
      front.cancel();
      back.cancel();
    };
  }, [activeIndex]);

  return (
    <section
      id="examples"
      ref={section}
      className={`examples-section page-width${scrollStages ? " has-scroll-stages" : ""}`}
      aria-labelledby="examples-title"
    >
      <div className="showcase-stage">
        <div className="showcase-introduction">
          <h2 id="examples-title" className="examples-title" tabIndex={-1}>
            {t("Bewegung zum Ausprobieren.")}
          </h2>
        </div>
        <div className="showcase-carousel">
          <div className="showcase-sidebar">
            <div
              className="showcase-categories"
              role="group"
              aria-label={t("Videotyp")}
            >
              {exampleGroups.map((group) => (
                <button
                  key={group.id}
                  aria-pressed={currentGroup.id === group.id}
                  onClick={() => {
                    if (currentGroup.id !== group.id)
                      goTo(
                        clips.findIndex((clip) => clip.group.id === group.id),
                      );
                  }}
                >
                  {t(group.title)}
                </button>
              ))}
            </div>
            <div className="showcase-copy" ref={categoryCopy}>
              <h3>{t(currentGroup.title)}</h3>
              <p>{t(currentGroup.description)}</p>
            </div>
          </div>
          <div className="showcase-media" ref={categoryVideos}>
            <div
              className="carousel-track"
              role="region"
              aria-label={t("Beispielvideos")}
            >
              {clips.map((clip, index) => (
                <div
                  key={clip.title}
                  ref={(element) => {
                    videoCards.current[index] = element;
                  }}
                  className={`carousel-slide${activeIndex === index ? " is-active" : ""}`}
                  role="group"
                  aria-roledescription={t("Folie")}
                  aria-label={`${index + 1} / ${clips.length}: ${clip.title}`}
                  hidden={clip.group !== currentGroup}
                >
                  <ExamplePreview
                    clip={clip}
                    selected={activeIndex === index}
                    onWatch={watch}
                    active={activeIndex === index && pageVisible && !viewer}
                    allowAutoplay={allowAutoplay && index % 2 === 0}
                    playRequested={userSelected === index}
                  />
                  {activeIndex !== index && (
                    <button
                      className="carousel-peek"
                      aria-label={`${t("Video anzeigen")}: ${clip.title}`}
                      onClick={() => {
                        setActiveIndex(index);
                        setUserSelected(index);
                      }}
                    />
                  )}
                </div>
              ))}
            </div>
            <div className="showcase-controls">
              <p className="preview-note">{t("Vorschau ohne Ton")}</p>
            </div>
          </div>
        </div>
      </div>
      <dialog
        ref={dialog}
        id="showcase-viewer"
        className="showcase-dialog"
        aria-labelledby="showcase-video-title"
        onCancel={(event) => {
          event.preventDefault();
          close();
        }}
        onClose={(event) => {
          if (event.currentTarget.open) return;
          fullVideo.current?.pause();
          setViewer(null);
          viewerOpener.current?.focus({ preventScroll: true });
        }}
        onClick={(event) => {
          if (event.target === event.currentTarget) close();
        }}
      >
        {viewer && (
          <>
            <div className="showcase-dialog-heading">
              <h3 id="showcase-video-title">{viewer.title}</h3>
              <button aria-label={t("Video schließen")} onClick={close}>
                <FiX aria-hidden="true" />
              </button>
            </div>
            {fullFailed ? (
              <div className="showcase-full-error">
                <p role="alert">
                  {t(
                    "Video konnte nicht geladen werden. Bitte versucht es später erneut.",
                  )}
                </p>
                <button
                  className="action action-primary"
                  onClick={() => {
                    setFullFailed(false);
                    setFullAttempt((attempt) => attempt + 1);
                  }}
                >
                  {t("Erneut versuchen")}
                </button>
              </div>
            ) : (
              <video
                ref={fullVideo}
                key={`${viewer.url}-${fullAttempt}`}
                className="showcase-full-video"
                style={{ "--video-aspect-ratio": fullAspectRatio }}
                src={viewer.url}
                poster={viewer.poster}
                controls
                controlsList="nofullscreen"
                playsInline
                autoPlay
                preload="metadata"
                aria-label={viewer.title}
                onError={() => setFullFailed(true)}
                onLoadedMetadata={(event) => {
                  const video = event.currentTarget;
                  if (video.videoWidth > 0 && video.videoHeight > 0)
                    setFullAspectRatio(video.videoWidth / video.videoHeight);
                }}
              />
            )}
          </>
        )}
      </dialog>
    </section>
  );
}
export default Examples;
